import React, { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from 'contexts/UserContext';
import { useClassroomContext } from 'contexts/ClassroomContext';
import userService from 'services/userService';
import userAuthService from 'services/userAuthService';
import useApiCall from 'contexts/ApiCall';
import ScreenReader from 'utils/ScreenReader';
import { SpeechRecognitionService } from 'services/speechRecognitionService';
import { getTokenExpiration } from 'utils/authUtils';
import User from 'models/User';
import useBadgePopup from 'contexts/celebrations/BadgePopup';
import useUserAlerts from 'contexts/UserAlerts';

interface AppPageProps {
  children?: ReactNode;
}

export const AppPage: React.FC<AppPageProps> = ({ children }) => {
  const { user, setUser } = useUserContext();
  const { classroom, setClassroom } = useClassroomContext();
  const { classroomId } = useParams();
  const { alerts, showAlertPopup, UserAlertModal } = useUserAlerts();
  const location = useLocation();
  const navigate = useNavigate();
  const makeApiCall = useApiCall();
  const { badges, showBadgePopup, BadgeModal } = useBadgePopup();

  useEffect(() => {
    if (user?.id) {
      // We have refresh and access tokens, so check if the access token is expired
      // if it is then log out the user
      // if it is not but it is close (under a certain threshold) get a new one at auth/refresh/
      // Threshold in milliseconds (e.g., 5 minutes = 5 * 60 * 1000 milliseconds)
      const threshold = 5 * 60 * 1000;
      const tokenExpiration = getTokenExpiration(user.token);
      // first check if the time left is within the threshold
      if (tokenExpiration < 0) {
        // logout the user
        console.debug('token expired');
        // window.location.reload();
        navigate('/logout');
      } else if (tokenExpiration <= threshold) {
        // get a new access token with the refresh token
        console.debug('refreshing token');
        userAuthService
          .refreshAccessToken(user.refresh_token)
          .then((respToken) =>
            setUser((prev: User | null) =>
              User.fromServerUser({ ...(prev ?? {}), token: respToken }),
            ),
          )
          .catch((error) => navigate('/logout'));
      }
      if (user?.earned_badges && user?.earned_badges.length) {
        user.earned_badges.forEach((b) => showBadgePopup(b));
        user.clearEarnedBadges();
      }
      return;
    }
    if (userAuthService.isAuthenticated()) {
      makeApiCall(userAuthService.getSessionUser)
        .then((respUser) => setUser(respUser))
        .catch((error) => navigate('/logout'));
    } else {
      setClassroom(null);
      navigate('/logout');
    }
    // eslint-disable-next-line
  }, [
    user?.earned_badges,
    user?.id,
    setUser,
    navigate,
    setClassroom,
    makeApiCall,
    showBadgePopup,
  ]);

  useEffect(() => {
    if (alerts) {
      const currentSegments = location.pathname.split('/').filter(Boolean);

      alerts.forEach((a) => {
        const alertSegments = a.path.split('/').filter(Boolean);

        // First, check if the beginning of the path matches
        const matchesBasePath = alertSegments.every(
          (segment, index) => segment === currentSegments[index],
        );

        // Second, ensure if the alert expects exact match (no extra path unless allowed)
        const isExactMatch =
          currentSegments.length === alertSegments.length ||
          (currentSegments.length === alertSegments.length + 1 &&
            /^\d+$/.test(currentSegments[currentSegments.length - 1]));

        if (matchesBasePath && isExactMatch) {
          showAlertPopup(a);
        }
      });
    }
  }, [location.pathname, alerts, showAlertPopup]);

  useEffect(() => {
    if (!badges) return;
    badges.forEach((b) => showBadgePopup(b));
  }, [badges, showBadgePopup]);

  useEffect(() => {
    if (classroom?.id) {
      ScreenReader.setLanguage(classroom.language);
      SpeechRecognitionService.setLanguage(classroom.language);
      return;
    }

    if (!user?.id || classroom?.id || !classroomId) return;
    makeApiCall(userService.getClassroomById, classroomId)
      .then((resp) => setClassroom(resp.classroom))
      .catch((error) => navigate(`/${user.account_type.toLowerCase()}/home`));
  }, [
    user?.id,
    user?.account_type,
    classroom?.id,
    classroom?.language,
    classroomId,
    setClassroom,
    navigate,
    makeApiCall,
  ]);

  return (
    <div className="app-page">
      {children} <BadgeModal /> <UserAlertModal />
    </div>
  );
};
